'use client';

import * as React from 'react';
import {
    ChevronDown,
    ChevronRight,
    Download,
    ExternalLink,
    FileText,
    Loader2,
    Minus,
    Plus,
    PencilLine,
    ArrowRight,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { trpc } from '@/lib/api/trpc';

// ─── Types (mirror the server diff payload) ─────────────────────────

type FeeChangeType = 'added' | 'removed' | 'changed';
interface FeeLineChange {
    key: string;
    label: string;
    changeType: FeeChangeType;
    before: string | null;
    after: string | null;
}

type ClauseChangeType = 'added' | 'removed' | 'modified';
interface ClauseChange {
    id: string;
    changeType: ClauseChangeType;
    titleBefore: string | null;
    titleAfter: string | null;
    textBefore: string | null;
    textAfter: string | null;
}

interface VersionDiff {
    feeChanges: FeeLineChange[];
    clauseChanges: ClauseChange[];
    introChanged: boolean;
    noteChanged: boolean;
    hasChanges: boolean;
}

interface VersionHistoryEntry {
    id: string;
    version: number;
    status: string;
    note: string | null;
    proposedByEmail: string | null;
    proposedAt: string | null;
    createdAt: string | null;
    effectiveAt: string | null;
    supersededAt: string | null;
    hasContractDocument: boolean;
    contractIsSigned: boolean;
    isInitial: boolean;
    diff: VersionDiff | null;
}

// ─── Status badge ───────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
    const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
        draft: { variant: 'outline', label: 'Draft' },
        pending_signature: { variant: 'secondary', label: 'Pending Signature' },
        active: { variant: 'default', label: 'Active' },
        superseded: { variant: 'outline', label: 'Superseded' },
        rejected: { variant: 'destructive', label: 'Rejected' },
        expired: { variant: 'destructive', label: 'Expired' },
        cancelled: { variant: 'outline', label: 'Cancelled' },
    };
    const config = variants[status] ?? { variant: 'outline' as const, label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
}

function fmtDate(v: string | null): string {
    if (!v) return '—';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

// ─── Contract PDF button ────────────────────────────────────────────

function base64PdfToObjectUrl(base64: string): string {
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
}

function ContractPdfButton({
    partnerSlug,
    isSuperAdmin,
    versionId,
    label,
}: {
    partnerSlug: string;
    isSuperAdmin: boolean;
    versionId: string;
    label: string;
}) {
    const utils = trpc.useUtils();
    const [loading, setLoading] = React.useState<'view' | 'download' | null>(null);

    const handleClick = async (mode: 'view' | 'download') => {
        setLoading(mode);
        try {
            if (isSuperAdmin) {
                // Works for every version, including drafts (PDF rendered on demand).
                const res = await utils.partnerBuyRate.getVersionDocument.fetch({ partnerSlug, versionId });
                if (res.kind === 'url' && res.documentUrl) {
                    window.open(res.documentUrl, '_blank', 'noopener,noreferrer');
                } else if (res.kind === 'pdf' && res.pdfBase64) {
                    const url = base64PdfToObjectUrl(res.pdfBase64);
                    if (mode === 'download') {
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = res.fileName ?? 'crm-buy-rate.pdf';
                        a.click();
                    } else {
                        window.open(url, '_blank', 'noopener,noreferrer');
                    }
                    setTimeout(() => URL.revokeObjectURL(url), 60_000);
                } else {
                    toast.error('Contract PDF not available yet');
                }
            } else {
                const res = await utils.partnerBuyRate.selfGetSignedDocument.fetch({ partner: partnerSlug });
                if (res?.documentUrl) {
                    window.open(res.documentUrl, '_blank', 'noopener,noreferrer');
                } else {
                    toast.error('Contract PDF not available yet');
                }
            }
        } catch (err: any) {
            toast.error(err?.message ?? 'Could not load the contract PDF');
        } finally {
            setLoading(null);
        }
    };

    return (
        <div className="flex items-center gap-1">
            <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1.5 px-2 text-xs"
                disabled={loading !== null}
                onClick={() => handleClick('view')}
            >
                {loading === 'view' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
                {label}
            </Button>
            {isSuperAdmin && (
                <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    title="Download PDF"
                    disabled={loading !== null}
                    onClick={() => handleClick('download')}
                >
                    {loading === 'download' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                </Button>
            )}
        </div>
    );
}

// ─── Fee change row ─────────────────────────────────────────────────

function FeeChangeRow({ change }: { change: FeeLineChange }) {
    return (
        <div className="flex items-center justify-between gap-3 py-1">
            <div className="flex items-center gap-2 text-sm">
                {change.changeType === 'added' && <Plus className="h-3.5 w-3.5 shrink-0 text-emerald-600" />}
                {change.changeType === 'removed' && <Minus className="h-3.5 w-3.5 shrink-0 text-rose-600" />}
                {change.changeType === 'changed' && <PencilLine className="h-3.5 w-3.5 shrink-0 text-amber-600" />}
                <span className="font-medium">{change.label}</span>
            </div>
            <div className="flex items-center gap-2 font-mono text-xs">
                {change.changeType === 'added' && (
                    <span className="text-emerald-700">{change.after}</span>
                )}
                {change.changeType === 'removed' && (
                    <span className="text-rose-700 line-through">{change.before}</span>
                )}
                {change.changeType === 'changed' && (
                    <>
                        <span className="text-muted-foreground line-through">{change.before}</span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        <span className="font-semibold text-foreground">{change.after}</span>
                    </>
                )}
            </div>
        </div>
    );
}

// ─── Clause change row (expandable old/new text) ────────────────────

function ClauseChangeRow({ change }: { change: ClauseChange }) {
    const [open, setOpen] = React.useState(false);
    const title = change.titleAfter ?? change.titleBefore ?? change.id;
    const renamed =
        change.changeType === 'modified' &&
        change.titleBefore != null &&
        change.titleAfter != null &&
        change.titleBefore !== change.titleAfter;

    const meta: Record<ClauseChangeType, { label: string; cls: string }> = {
        added: { label: 'Added', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
        removed: { label: 'Removed', cls: 'text-rose-700 bg-rose-50 border-rose-200' },
        modified: { label: 'Modified', cls: 'text-amber-700 bg-amber-50 border-amber-200' },
    };

    return (
        <div className="rounded-md border">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/40"
            >
                {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                <span
                    className={`rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${meta[change.changeType].cls}`}
                >
                    {meta[change.changeType].label}
                </span>
                <span className="flex-1 truncate font-medium">
                    {renamed ? (
                        <span>
                            <span className="text-muted-foreground line-through">{change.titleBefore}</span>{' '}
                            <ArrowRight className="inline h-3 w-3 text-muted-foreground" /> {change.titleAfter}
                        </span>
                    ) : (
                        title
                    )}
                </span>
            </button>
            {open && (
                <div className="space-y-3 border-t px-3 py-3 text-sm">
                    {change.textBefore != null && (
                        <div>
                            <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-rose-600">
                                {change.changeType === 'modified' ? 'Before' : 'Removed text'}
                            </p>
                            <p className="whitespace-pre-wrap rounded bg-rose-50/60 p-2 text-xs leading-relaxed text-foreground/80">
                                {change.textBefore}
                            </p>
                        </div>
                    )}
                    {change.textAfter != null && (
                        <div>
                            <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-emerald-600">
                                {change.changeType === 'modified' ? 'After' : 'New text'}
                            </p>
                            <p className="whitespace-pre-wrap rounded bg-emerald-50/60 p-2 text-xs leading-relaxed text-foreground/90">
                                {change.textAfter}
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── What-changed panel ─────────────────────────────────────────────

function ChangeSummary({ diff }: { diff: VersionDiff }) {
    const parts: string[] = [];
    if (diff.feeChanges.length > 0) {
        parts.push(`${diff.feeChanges.length} fee ${diff.feeChanges.length === 1 ? 'change' : 'changes'}`);
    }
    if (diff.clauseChanges.length > 0) {
        parts.push(`${diff.clauseChanges.length} clause ${diff.clauseChanges.length === 1 ? 'change' : 'changes'}`);
    }
    if (diff.introChanged) parts.push('intro updated');
    if (diff.noteChanged) parts.push('note updated');
    return <>{parts.join(' · ')}</>;
}

function WhatChanged({ diff }: { diff: VersionDiff }) {
    const [open, setOpen] = React.useState(false);

    if (!diff.hasChanges) {
        return (
            <p className="text-xs text-muted-foreground">
                No pricing or clause changes vs the previous version.
            </p>
        );
    }

    return (
        <div className="rounded-lg border bg-muted/20">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium hover:bg-muted/40"
            >
                {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <span>What changed</span>
                <span className="text-xs font-normal text-muted-foreground">
                    <ChangeSummary diff={diff} />
                </span>
            </button>
            {open && (
                <div className="space-y-4 border-t px-3 py-3">
                    {diff.feeChanges.length > 0 && (
                        <div>
                            <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                Fee schedule
                            </p>
                            <div className="divide-y rounded-md border px-3">
                                {diff.feeChanges.map((c) => (
                                    <FeeChangeRow key={c.key} change={c} />
                                ))}
                            </div>
                        </div>
                    )}
                    {diff.clauseChanges.length > 0 && (
                        <div>
                            <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                Legal clauses
                            </p>
                            <div className="space-y-2">
                                {diff.clauseChanges.map((c) => (
                                    <ClauseChangeRow key={c.id} change={c} />
                                ))}
                            </div>
                        </div>
                    )}
                    {(diff.introChanged || diff.noteChanged) && (
                        <div className="flex flex-wrap gap-2">
                            {diff.introChanged && (
                                <Badge variant="outline" className="text-amber-700">
                                    Contract intro updated
                                </Badge>
                            )}
                            {diff.noteChanged && (
                                <Badge variant="outline" className="text-amber-700">
                                    Proposal note updated
                                </Badge>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Timeline ───────────────────────────────────────────────────────

export function CrmBuyRateVersionHistory({
    partnerSlug,
    isSuperAdmin,
}: {
    partnerSlug: string;
    isSuperAdmin: boolean;
}) {
    const adminQuery = trpc.partnerBuyRate.listVersionHistory.useQuery(
        { partnerSlug },
        { enabled: isSuperAdmin && partnerSlug.length > 0 },
    );
    const selfQuery = trpc.partnerBuyRate.selfListVersionHistory.useQuery(
        { partner: partnerSlug },
        { enabled: !isSuperAdmin && partnerSlug.length > 0 },
    );
    const query = isSuperAdmin ? adminQuery : selfQuery;
    const entries = (query.data ?? []) as VersionHistoryEntry[];

    if (query.isLoading) {
        return (
            <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-24 w-full" />
                ))}
            </div>
        );
    }

    if (entries.length === 0) {
        return (
            <p className="text-sm text-muted-foreground">
                {isSuperAdmin
                    ? 'No versions yet. Use "Propose New Rate" to create and send one.'
                    : 'No contract versions have been sent to you yet.'}
            </p>
        );
    }

    return (
        <ol className="relative space-y-4 border-l pl-6">
            {entries.map((entry) => (
                <li key={entry.id} className="relative">
                    {/* timeline dot */}
                    <span
                        className={`absolute -left-[27px] top-1.5 h-3 w-3 rounded-full border-2 border-background ${
                            entry.status === 'active'
                                ? 'bg-emerald-500'
                                : entry.status === 'pending_signature'
                                  ? 'bg-amber-500'
                                  : 'bg-muted-foreground/40'
                        }`}
                    />
                    <div className="space-y-3 rounded-lg border p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="font-mono text-sm font-semibold">v{entry.version}</span>
                                <StatusBadge status={entry.status} />
                                {entry.isInitial && (
                                    <Badge variant="secondary" className="text-xs">
                                        Initial version
                                    </Badge>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {(isSuperAdmin || entry.hasContractDocument) && (
                                    <ContractPdfButton
                                        partnerSlug={partnerSlug}
                                        isSuperAdmin={isSuperAdmin}
                                        versionId={entry.id}
                                        label={
                                            entry.contractIsSigned
                                                ? 'Signed PDF'
                                                : entry.status === 'draft'
                                                  ? 'Draft PDF'
                                                  : 'View PDF'
                                        }
                                    />
                                )}
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            <span className="inline-flex items-center gap-1">
                                <FileText className="h-3 w-3" />
                                {entry.contractIsSigned && entry.effectiveAt
                                    ? `Signed ${fmtDate(entry.effectiveAt)}`
                                    : `Proposed ${fmtDate(entry.proposedAt ?? entry.createdAt)}`}
                            </span>
                            {isSuperAdmin && entry.proposedByEmail && <span>by {entry.proposedByEmail}</span>}
                        </div>

                        {entry.note && (
                            <p className="rounded-md bg-muted/40 px-3 py-2 text-sm text-foreground/80">{entry.note}</p>
                        )}

                        {entry.isInitial ? (
                            <p className="text-xs text-muted-foreground">
                                First version{isSuperAdmin ? '' : ' proposed to you'} — nothing to compare against.
                            </p>
                        ) : entry.diff ? (
                            <WhatChanged diff={entry.diff} />
                        ) : null}
                    </div>
                </li>
            ))}
        </ol>
    );
}
